import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/PasswordInput";
import { Link } from "react-router-dom";
import { Wrench, ShieldCheck, UserCircle, Briefcase, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1664329182818-f375993c0a00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJhY2tncm91bmQlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc4MjMyMDc5NHww&ixlib=rb-4.1.0&q=85";

const TYPES = [
  { key: "customer", label: "Customer", icon: UserCircle, desc: "Book & track services" },
  { key: "partner", label: "Partner", icon: Briefcase, desc: "Manage your jobs" },
  { key: "fixigo", label: "Fixigo", icon: Buildings, desc: "Staff / Admin panel" },
];

const DEMO = {
  fixigo: [["Admin", "admin@fixigo.com", "Admin@123"], ["Finance", "finance@fixigo.com", "Finance@123"], ["Ops", "ops@fixigo.com", "Ops@123"]],
  partner: [["Partner", "partner@fixigo.com", "Partner@123"]],
  customer: [["Customer", "customer@fixigo.com", "Customer@123"]],
};

export default function Login() {
  const { login, registerCustomer } = useAuth();
  const initialType = new URLSearchParams(window.location.search).get("type");
  const [type, setType] = useState(["customer", "partner", "fixigo"].includes(initialType) ? initialType : "customer");
  const isPreview = typeof window !== "undefined" &&
    (window.location.hostname.includes("preview") || window.location.hostname.includes("localhost"));
  const [mode, setMode] = useState("login"); // login | register (customer only)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reg, setReg] = useState({ name: "", email: "", password: "", phone: "", city: "" });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  const redirect = (u) => {
    const params = new URLSearchParams(window.location.search);
    const next = params.get("next");
    if (u.role === "partner") { window.location.href = "/portal"; return; }
    if (u.role === "customer") { window.location.href = next || "/account"; return; }
    window.location.href = "/admin";
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { redirect(await login(email, password)); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error("Please accept the Terms & Conditions to continue"); return; }
    setLoading(true);
    try { redirect(await registerCustomer({ ...reg, accept_terms: true })); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Registration failed"); }
    finally { setLoading(false); }
  };

  const quick = (em, pw) => { setEmail(em); setPassword(pw); setMode("login"); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-primary">
        <img src={LOGIN_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-primary-foreground rounded-sm flex items-center justify-center">
              <Wrench size={20} weight="bold" className="text-primary" />
            </div>
            <span className="font-heading font-bold text-xl">Fixigo</span>
          </div>
          <div>
            <h2 className="font-heading text-4xl font-bold tracking-tight leading-tight">
              One platform.<br />Three ways in.
            </h2>
            <p className="mt-4 text-primary-foreground/70 max-w-md">
              Customers book services, partners manage jobs, and the Fixigo team runs operations & finance.
            </p>
          </div>
          <div className="flex items-center gap-2 text-primary-foreground/70 text-sm">
            <ShieldCheck size={18} weight="bold" /> Role-based secure access
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
              <Wrench size={20} weight="bold" className="text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl">Fixigo</span>
          </div>

          {/* 3-way chooser */}
          <p className="label-caps mb-3">I am a</p>
          <div className="grid grid-cols-3 gap-2 mb-8" data-testid="login-type-chooser">
            {TYPES.map((t) => (
              <button key={t.key} type="button" data-testid={`login-type-${t.key}`}
                onClick={() => { setType(t.key); setMode("login"); setPassword(""); }}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-sm border transition-colors duration-200 ${
                  type === t.key ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-secondary text-muted-foreground"
                }`}>
                <t.icon size={22} weight={type === t.key ? "fill" : "regular"} />
                <span className="text-xs font-semibold">{t.label}</span>
              </button>
            ))}
          </div>

          <h1 className="font-heading text-2xl font-bold tracking-tight">
            {type === "customer" && mode === "register" ? "Create your account" : `${TYPES.find((t) => t.key === type).label} sign in`}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 mb-6">{TYPES.find((t) => t.key === type).desc}</p>

          {type === "customer" && mode === "register" ? (
            <form onSubmit={doRegister} className="space-y-3" data-testid="register-form">
              <div><Label className="label-caps">Full Name</Label>
                <Input data-testid="reg-name" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} className="rounded-sm mt-1.5" required /></div>
              <div><Label className="label-caps">Email</Label>
                <Input data-testid="reg-email" type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} className="rounded-sm mt-1.5" required /></div>
              <div><Label className="label-caps">Password</Label>
                <PasswordInput data-testid="reg-password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} className="rounded-sm mt-1.5" required /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">Phone</Label>
                  <Input data-testid="reg-phone" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} className="rounded-sm mt-1.5" /></div>
                <div><Label className="label-caps">City</Label>
                  <Input data-testid="reg-city" value={reg.city} onChange={(e) => setReg({ ...reg, city: e.target.value })} className="rounded-sm mt-1.5" /></div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer select-none pt-1" data-testid="reg-terms-label">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border accent-primary" data-testid="reg-terms-checkbox" required />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  I agree to the{" "}
                  <Link to="/customer-terms" target="_blank" className="text-primary font-semibold hover:underline" data-testid="reg-terms-link">Customer Terms &amp; Conditions</Link>.
                </span>
              </label>
              <Button data-testid="register-submit" type="submit" disabled={loading || !agree} className="w-full rounded-sm font-semibold">
                {loading ? "Creating…" : "Create account"}
              </Button>
              <p className="text-sm text-center text-muted-foreground">
                Already have an account?{" "}
                <button type="button" className="text-primary font-semibold" onClick={() => setMode("login")} data-testid="switch-to-login">Sign in</button>
              </p>
            </form>
          ) : (
            <form onSubmit={doLogin} className="space-y-4" data-testid="login-form">
              <div><Label className="label-caps">Email / User ID</Label>
                <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your registered email" className="rounded-sm mt-1.5" required /></div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="label-caps">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary font-semibold hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                </div>
                <PasswordInput data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-sm mt-1.5" required /></div>
              <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-sm bg-primary hover:bg-primary/90 font-semibold">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              {type === "customer" && (
                <p className="text-sm text-center text-muted-foreground">
                  New customer?{" "}
                  <button type="button" className="text-primary font-semibold" onClick={() => setMode("register")} data-testid="switch-to-register">Create an account</button>
                </p>
              )}
            </form>
          )}

          <div className="mt-8 border-t border-border pt-5">
            {isPreview ? (
              <>
                <p className="label-caps mb-3">Demo {TYPES.find((t) => t.key === type).label} accounts</p>
                <div className="space-y-2 text-sm">
                  {DEMO[type].map(([r, em, pw]) => (
                    <button key={em} type="button" data-testid={`demo-${r.toLowerCase()}`} onClick={() => quick(em, pw)}
                      className="flex justify-between w-full px-3 py-2 rounded-sm border border-border hover:bg-secondary transition-colors duration-200">
                      <span className="font-semibold">{r}</span>
                      <span className="text-muted-foreground">{em}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <a href="/" className="text-sm text-muted-foreground hover:text-foreground">← Back to Fixigo Home</a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
